import { Button, Checkbox, Hairline, Label, Radio, Steps } from "@components";
import classNames from "classnames/bind";
import { EnvironmentsAPI } from "providers/EnvironmentsAPI";
import { useEffect, useRef, useState } from "react";
import { ForEach, If } from "shared/components/control-statements";
import { SuccessIcon } from "shared/components/Icons";
import { InputSearch } from "shared/components/InputSearch";
import { EnvironmentStorage } from "shared/tools/EnvironmentStorage";
import { UserStorage } from "shared/tools/UserStorage";
import styles from "styles/replicate-catalog.module.scss";
import { Drawer } from "../../shared/components/Drawer";
import { CatalogAPI } from "providers/CatalogAPI";
const css = classNames.bind(styles);

const steps = {
  BRANDS: 1,
  PLACES: 2,
  REVIEW: 3,
  SUCCESS: 4,
};

const places = {
  DELIVERY: "Delivery",
  INDOOR: "Indoor",
  BOTH: "Ambas (Delivery / Indoor)",
};

export const ReplicateCatalog = ({
  visible,
  onClose,
  onSave,
  catalogService = "",
}) => {
  const [search, setSearch] = useState("");
  const [environments, setEnvironments] = useState([]);
  const [selectedEnvironments, setSelectedEnvironments] = useState([]);

  const [currentStep, setCurrentStep] = useState(1);
  const [collapsed, setCollapsed] = useState(false);
  const [selectedDestiny, setSelectedDestiny] = useState(null);
  const [loading, setLoading] = useState(false);
  const [timeLeft, setTimeLeft] = useState(5);

  const timerRef = useRef(null);

  const environment = EnvironmentStorage.getEnvironment();

  const filteredEnvironments = environments.filter((c) => {
    return !!search
      ? c.label?.toLowerCase().includes(search.toLowerCase())
      : true;
  });

  const allEnvironmentsSelected =
    selectedEnvironments.length === environments.length;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const user = UserStorage.getLoggedUser();
        const unitsResponse = await EnvironmentsAPI.getAvailableUnits(
          user.environment_id
        );

        const units = unitsResponse.getData([]);

        const currentBrandId = EnvironmentStorage.getEnvironmentBrandId();
        const brandUnits = units.filter(
          (unit) => unit.brand.id === currentBrandId
        );

        const environmentsMap = new Map();

        brandUnits.forEach((unit) => {
          const { brand } = unit;

          const brandKey = unit.brand.id;
          if (!environmentsMap.has(brandKey)) {
            environmentsMap.set(brandKey, {
              id: brand.id,
              name: brand.name,
              alias: brand.alias,
              type: "BRAND",
              label: brand.name,
            });
          }

          const unitKey = unit.id;
          if (!environmentsMap.has(unitKey)) {
            environmentsMap.set(unitKey, {
              id: unit.id,
              name: unit.name,
              alias: unit.alias,
              type: "UNIT",
              label: `${brand.name} / ${unit.name}`,
            });
          }
        });

        const environments = Array.from(environmentsMap.values()).sort((a, b) =>
          a.label.localeCompare(b.label)
        );
        setEnvironments(environments);
      } catch (error) {
        console.error(error);
      }
    };

    fetchData();
  }, []);

  const getTitle = () => {
    switch (currentStep) {
      case steps.BRANDS:
      case steps.PLACES:
        return "Replicar cardápio";
      case steps.REVIEW:
        return "Confirmar réplica";
      case steps.SUCCESS:
        return "Cardápio replicado com sucesso";
      default:
        return "";
    }
  };

  const getSubtitle = () => {
    switch (currentStep) {
      case steps.BRANDS:
      case steps.PLACES:
        return "Selecione o destino";
      case steps.REVIEW:
        return "";
      case steps.SUCCESS:
        return "Seu cardápio foi replicado";
      default:
        return "";
    }
  };

  const getCatalogPlace = () => {
    return [environment?.brand?.name, environment?.name, catalogService]
      .filter(Boolean)
      .join(" / ")
      .toLowerCase();
  };

  const save = async () => {
    setLoading(true);
    const targetServices = [];

    const confirmed = await window.confirm({
      text: "Você tem certeza que deseja realizar a cópia? Esta ação não poderá ser desfeita.",
      confirmText: "Copiar",
      cancelText: "Cancelar",
    });

    if (!confirmed) {
      setLoading(false);
      return;
    }

    if (selectedDestiny === places.BOTH) {
      targetServices.push("DELIVERY", "INDOOR");
    } else {
      targetServices.push(selectedDestiny.toUpperCase());
    }

    const sourceEnvironment = EnvironmentStorage.getEnvironment().id;

    const response = await CatalogAPI.copyCatalog({
      sourceEnvironment: sourceEnvironment,
      sourceService: catalogService,
      targetEnvironments: selectedEnvironments.map((e) => e.id),
      targetServices,
    });

    if (response.getErrors([]).length > 0) {
      setLoading(false);

      const isCommonError = typeof response.getErrors()[0] === "string";
      if (isCommonError) {
        return alert({ text: response.getErrors()[0], type: "error" });
      }

      return alert({
        text: response.getErrors().map((error) => (
          <p>
            {error.environmentId} - {error.catalog} - {error.error}
          </p>
        )),
      });
    }

    setCurrentStep(steps.SUCCESS);
    setLoading(false);
    onSave();
  };

  const reset = () => {
    onClose();
    setCurrentStep(1);
    setSelectedEnvironments([]);
    setSelectedDestiny(null);
    setLoading(false);
  };

  const startTimer = () => {
    timerRef.current = setInterval(() => {
      setTimeLeft((prevTime) => {
        if (prevTime <= 1) {
          clearInterval(timerRef.current);
          return 0;
        }
        return prevTime - 1;
      });
    }, 1000);
  };

  useEffect(() => {
    if (currentStep === steps.REVIEW) startTimer();

    return () => clearInterval(timerRef.current);
  }, [currentStep]);

  return (
    <Drawer
      visible={visible}
      onClose={reset}
      title={getTitle()}
      showCloseButton={currentStep !== steps.SUCCESS}
      showBackButton={currentStep !== steps.SUCCESS}
      backButton={
        currentStep === steps.BRANDS
          ? () => onClose()
          : () => setCurrentStep((prev) => prev - 1)
      }
      subtitle={getSubtitle()}
      withoutTransition
    >
      <If cond={currentStep === steps.BRANDS || currentStep === steps.PLACES}>
        <Steps steps={2} currentStep={currentStep} />
      </If>

      <If cond={currentStep === steps.BRANDS}>
        <div className="d-flex align-center justify-between">
          <Label className="mb-0">
            Selecione em que unidade deseja replicar este cardápio
          </Label>
          <div className="d-flex align-center">
            <Checkbox value={true} checked={allEnvironmentsSelected} readOnly />
            <div className={styles.collapsible}>
              <div
                className={styles.chevron}
                onClick={() => setCollapsed(!collapsed)}
              >
                <i className="fas fa-chevron-down" />
              </div>

              <div
                className={css({
                  collapsed: true,
                  in: collapsed,
                  out: !collapsed,
                })}
              >
                <ul className={styles.options}>
                  <li
                    onClick={() => {
                      setSelectedEnvironments(environments);
                      setCollapsed(false);
                    }}
                  >
                    Selecionar tudo
                  </li>
                  <li
                    onClick={() => {
                      setSelectedEnvironments([]);
                      setCollapsed(false);
                    }}
                  >
                    Desmarcar tudo
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div className={styles.catalogPlace}>{getCatalogPlace()}</div>

        <InputSearch
          placeholder="Buscar por marca ou unidade..."
          design="two"
          occurrences={(filteredEnvironments || []).length}
          onChange={(e) => setSearch(e.target.value)}
        />

        <div className={styles.brandsAndUnits}>
          <ForEach
            items={filteredEnvironments}
            render={(item) => {
              return (
                <div
                  key={item.alias}
                  className={css({
                    brand: true,
                    selected: selectedEnvironments.find(
                      (c) => c.id === item.id
                    ),
                  })}
                  onClick={() =>
                    setSelectedEnvironments((prev) => {
                      if (prev.find((c) => c.id === item.id)) {
                        return prev.filter((c) => c.id !== item.id);
                      } else {
                        return [...prev, item];
                      }
                    })
                  }
                >
                  <Checkbox
                    value={item.id}
                    checked={selectedEnvironments.find((c) => c.id === item.id)}
                    readOnly
                  />
                  <p className={styles.name}>{item.label}</p>
                </div>
              );
            }}
          />
        </div>
        <div className={styles.footer}>
          <Button
            design="primary"
            className="w-100"
            disabled={selectedEnvironments.length === 0}
            onClick={() => setCurrentStep(steps.PLACES)}
          >
            Próximo
          </Button>
        </div>
      </If>

      <If cond={currentStep === steps.PLACES}>
        <Label className="mb-0">
          Selecione em que tipo de frente deseja replicar este cardápio
        </Label>
        <Hairline className="mb-20 mt-20" />
        <Radio
          className="mb-10"
          family="destiny"
          checked={selectedDestiny}
          value={places.DELIVERY}
          onChange={() => setSelectedDestiny(places.DELIVERY)}
        >
          Delivery
        </Radio>
        <Radio
          className="mb-10"
          family="destiny"
          checked={selectedDestiny}
          value={places.INDOOR}
          onChange={() => setSelectedDestiny(places.INDOOR)}
        >
          Indoor
        </Radio>
        <Radio
          family="destiny"
          checked={selectedDestiny}
          value={places.BOTH}
          onChange={() => setSelectedDestiny(places.BOTH)}
        >
          Ambas
        </Radio>

        <div className={styles.footer}>
          <Button
            design="primary"
            className="w-100"
            disabled={!selectedDestiny}
            onClick={() => setCurrentStep(steps.REVIEW)}
          >
            Próximo
          </Button>
        </div>
      </If>

      <If cond={currentStep === steps.REVIEW}>
        <Label>Confirme a réplica do cardápio abaixo</Label>

        <div className={styles.scrollContainer}>
          <div className={styles.replicated}>
            <div className="d-flex align-center w-100 justify-between">
              <p className={styles.title}>Será replicado</p>
            </div>
            <p className={styles.place}>
              {environment?.brand?.name || environment?.name} /{" "}
              {catalogService?.charAt(0).toUpperCase() +
                catalogService?.slice(1).toLowerCase()}
            </p>
          </div>

          <div className={styles.replicated}>
            <div className="d-flex align-center w-100 justify-between">
              <p className={styles.title}>Destino da réplica</p>
            </div>
            <p className={styles.brands}>
              <ForEach
                items={selectedEnvironments}
                render={(environment) => (
                  <span className={styles.brand} key={environment.id}>
                    {environment.label}
                  </span>
                )}
              />
            </p>
            <p className={styles.destiny}>{selectedDestiny}</p>
          </div>
        </div>

        <div className={styles.footer}>
          <Button
            design="primary"
            className="w-100"
            onClick={save}
            disabled={loading}
            loading={loading}
          >
            Copiar cardápio
          </Button>
          <Button
            design="two"
            className="w-100 mt-10"
            disabled={loading}
            onClick={() => setCurrentStep(steps.PLACES)}
          >
            Cancelar
          </Button>
        </div>
      </If>

      <If cond={currentStep === steps.SUCCESS}>
        <div className={styles.success}>
          <div className={styles.successIcon}>
            <SuccessIcon color="#1f1f1f" size={14} />
          </div>
        </div>
        <div className={styles.footer}>
          <Button design="primary" className="w-100" onClick={reset}>
            Fechar
          </Button>
        </div>
      </If>
    </Drawer>
  );
};
